import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, Card, Input, Badge, Spinner } from '@realvisor/ui';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { ROLE_PERMISSIONS, type Permission } from '@realvisor/shared';
import { api } from '@/lib/api';

// ── Role definitions ────────────────────────────────────────────
const CRM_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Vlastník tenantu, plná kontrola', color: 'bg-purple-100 text-purple-700' },
  { value: 'admin', label: 'Admin', description: 'Administrátor, může spravovat vše kromě fakturace', color: 'bg-blue-100 text-blue-700' },
  { value: 'leader', label: 'Leader', description: 'Vedoucí týmu, může spravovat projekty a členy', color: 'bg-green-100 text-green-700' },
  { value: 'agent', label: 'Agent', description: 'Makléř, pracuje s leady a kontakty', color: 'bg-orange-100 text-orange-700' },
  { value: 'assistant', label: 'Assistant', description: 'Asistent, omezená práva (jen čtení)', color: 'bg-gray-100 text-gray-700' },
];

const CC_ROLES = [
  { value: 'cc_manager', label: 'CC Manažer', description: 'Konfigurace SIP, správa CC týmu, pipeline read-only', color: 'bg-pink-100 text-pink-700' },
  { value: 'cc_supervisor', label: 'CC Supervizor', description: 'Přidělování kontaktů, statistiky, všechny kontakty/leady', color: 'bg-rose-100 text-rose-700' },
  { value: 'cc_operator', label: 'CC Operátor', description: 'Volání, pouze přidělené kontakty a leady', color: 'bg-red-100 text-red-700' },
];

const ALL_ROLES = [...CRM_ROLES, ...CC_ROLES];

// Permission labels for display
const PERMISSION_LABELS: Record<string, string> = {
  softphone: 'Softphone',
  'contacts:own': 'Kontakty (vlastní)',
  'contacts:all': 'Kontakty (vše)',
  'leads:own': 'Leady (vlastní)',
  'leads:all': 'Leady (vše)',
  'leads:assign': 'Přidělování leadů',
  'activities:own': 'Aktivity (vlastní)',
  'activities:all': 'Aktivity (vše)',
  'call_scripts:read': 'Call skripty (čtení)',
  'call_scripts:write': 'Call skripty (zápis)',
  call_stats: 'Call statistiky',
  'pipeline:read': 'Pipeline (čtení)',
  'pipeline:write': 'Pipeline (zápis)',
  'sip:config': 'SIP konfigurace',
  'cc_users:manage': 'Správa CC uživatelů',
  forms: 'Formuláře',
  email: 'Email',
  properties: 'Nemovitosti',
  discovery: 'Discovery',
  mortgages: 'Hypotéky',
  guides: 'Průvodci',
  settings: 'Nastavení',
  'projects:manage': 'Správa projektů',
  'projects:read': 'Projekty (čtení)',
  'users:manage': 'Správa uživatelů',
};

interface User {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  role: string;
  createdAt: string;
}

// ── Permission badge grid for a role ────────────────────────────
function RolePermissions({ role }: { role: string }) {
  const perms = ROLE_PERMISSIONS[role];
  if (!perms) return null;

  const isWildcard = perms.includes('*' as any);
  if (isWildcard) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
        <ShieldCheckIcon className="h-3 w-3" /> Plný přístup
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {(perms as readonly Permission[]).map((p) => (
        <span
          key={p}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600"
        >
          {PERMISSION_LABELS[p] ?? p}
        </span>
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────
export default function UserManagement() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPermsFor, setShowPermsFor] = useState<string | null>(null);

  const canManageAll = can('users:manage');
  const canManageCC = can('cc_users:manage');

  // Available roles depend on caller's permission level
  const availableRoles = canManageAll ? ALL_ROLES : CC_ROLES;

  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    phone: '',
    password: '',
    role: canManageAll ? 'agent' : 'cc_operator',
  });

  // Load users
  const { data: users, isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => api.get<User[]>('/users'),
  });

  // Access gate — need either users:manage or cc_users:manage
  if (!canManageAll && !canManageCC) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card padding="lg">
          <p className="text-red-600">Nemáte oprávnění k této stránce.</p>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.fullName || !formData.password) {
      toast.error('Vyplňte všechna povinná pole');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/users', {
        email: formData.email,
        fullName: formData.fullName,
        phone: formData.phone || null,
        password: formData.password,
        role: formData.role,
      });
      toast.success(`Uživatel ${formData.fullName} byl úspěšně vytvořen`);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      resetForm();
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se vytvořit uživatele');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || !formData.fullName) {
      toast.error('Vyplňte všechna povinná pole');
      return;
    }
    setIsLoading(true);
    try {
      await api.put(`/users/${editingUser.id}`, {
        fullName: formData.fullName,
        phone: formData.phone || null,
        role: formData.role,
        ...(formData.password && { password: formData.password }),
      });
      toast.success(`Uživatel ${formData.fullName} byl úspěšně aktualizován`);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      resetForm();
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se aktualizovat uživatele');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (u: User) => {
    setEditingUser(u);
    setFormData({
      email: u.email,
      fullName: u.fullName,
      phone: u.phone || '',
      password: '',
      role: u.role,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (userId: string, userName: string) => {
    if (!confirm(`Opravdu chcete smazat uživatele ${userName}?`)) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`Uživatel ${userName} byl úspěšně smazán`);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se smazat uživatele');
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setFormData({
      email: '',
      fullName: '',
      phone: '',
      password: '',
      role: canManageAll ? 'agent' : 'cc_operator',
    });
  };

  const getRoleMeta = (roleValue: string) =>
    ALL_ROLES.find((r) => r.value === roleValue);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/settings">
            <Button variant="outline" size="sm">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {canManageAll ? 'Správa uživatelů' : 'Správa CC týmu'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {canManageAll
                ? 'Přidávání a správa všech uživatelů v systému'
                : 'Správa operátorů a supervizorů call centra'}
            </p>
          </div>
        </div>
        {!showAddForm && (
          <Button
            onClick={() => {
              setEditingUser(null);
              setShowAddForm(true);
            }}
            leftIcon={<PlusIcon className="h-5 w-5" />}
          >
            Přidat uživatele
          </Button>
        )}
      </div>

      {/* Add / Edit form */}
      {showAddForm && (
        <Card padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <UserPlusIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {editingUser ? 'Upravit uživatele' : 'Nový uživatel'}
            </h2>
          </div>

          <form
            onSubmit={editingUser ? handleUpdate : handleSubmit}
            className="space-y-6"
          >
            {/* Basic fields */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="E-mail *"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="novyuzivatel@example.com"
                required
                disabled={!!editingUser}
                hint={editingUser ? 'Email nelze změnit' : undefined}
              />
              <Input
                label="Celé jméno *"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                placeholder="Jan Novák"
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Telefon"
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+420 123 456 789"
              />
              <Input
                label={editingUser ? 'Nové heslo (volitelné)' : 'Heslo *'}
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                placeholder="Minimálně 8 znaků"
                required={!editingUser}
                hint={
                  editingUser
                    ? 'Vyplňte pouze pokud chcete změnit heslo'
                    : 'Uživatel si může heslo později změnit'
                }
              />
            </div>

            {/* Role picker — grouped */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Role v systému *
              </label>

              {canManageAll && (
                <p className="text-xs text-gray-500 mb-2">CRM role</p>
              )}
              {canManageAll && (
                <div className="grid gap-3 sm:grid-cols-2 mb-4">
                  {CRM_ROLES.map((role) => (
                    <RoleButton
                      key={role.value}
                      role={role}
                      selected={formData.role === role.value}
                      onSelect={() =>
                        setFormData({ ...formData, role: role.value })
                      }
                    />
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-500 mb-2">Call centrum role</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {CC_ROLES.map((role) => (
                  <RoleButton
                    key={role.value}
                    role={role}
                    selected={formData.role === role.value}
                    onSelect={() =>
                      setFormData({ ...formData, role: role.value })
                    }
                  />
                ))}
              </div>
            </div>

            {/* Permission preview for selected role */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Oprávnění role{' '}
                <span className="font-bold">
                  {getRoleMeta(formData.role)?.label ?? formData.role}
                </span>
                :
              </p>
              <RolePermissions role={formData.role} />
            </div>

            {/* Info box */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-700">
                <strong>Co se stane:</strong>
              </p>
              <ul className="mt-2 text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>Uživatel bude vytvořen v Supabase Auth</li>
                <li>
                  Bude přidán do tenantu s rolí{' '}
                  <strong>
                    {getRoleMeta(formData.role)?.label ?? formData.role}
                  </strong>
                </li>
                <li>
                  Bude automaticky přidán do všech projektů jako{' '}
                  <strong>Member</strong>
                </li>
                <li>Obdrží e-mail s přihlašovacími údaji</li>
              </ul>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button type="button" variant="outline" onClick={resetForm}>
                Zrušit
              </Button>
              <Button type="submit" isLoading={isLoading}>
                {isLoading
                  ? editingUser
                    ? 'Ukládám...'
                    : 'Vytvářím...'
                  : editingUser
                    ? 'Uložit změny'
                    : 'Vytvořit uživatele'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* User list */}
      {!showAddForm && (
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {canManageAll ? 'Uživatelé v systému' : 'Call centrum tým'}
          </h2>

          {usersLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : users && users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Jméno
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Oprávnění
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Akce
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => {
                    const meta = getRoleMeta(u.role);
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {u.fullName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {u.phone || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            className={
                              meta?.color || 'bg-gray-100 text-gray-700'
                            }
                          >
                            {meta?.label || u.role}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            className="text-xs text-primary-600 hover:underline"
                            onClick={() =>
                              setShowPermsFor(
                                showPermsFor === u.id ? null : u.id,
                              )
                            }
                          >
                            {showPermsFor === u.id ? 'Skrýt' : 'Zobrazit'}
                          </button>
                          {showPermsFor === u.id && (
                            <RolePermissions role={u.role} />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => handleEdit(u)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Upravit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            {u.id !== user?.id && (
                              <button
                                onClick={() => handleDelete(u.id, u.fullName)}
                                className="text-red-600 hover:text-red-900"
                                title="Smazat"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Zatím žádní uživatelé
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ── Role selector button ────────────────────────────────────────
function RoleButton({
  role,
  selected,
  onSelect,
}: {
  role: { value: string; label: string; description: string; color: string };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`p-4 rounded-lg border-2 text-left transition-all ${
        selected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-900">{role.label}</span>
        <Badge className={role.color}>{role.value}</Badge>
      </div>
      <p className="text-sm text-gray-600">{role.description}</p>
    </button>
  );
}
